import { QueryHookOptions, useLazyQuery, useQuery } from '@apollo/client';
import { TRANSLATE_ARTICLE } from 'src/services/graphql/translate/query';
import { useAlert } from './useAlert';
import { useAppDispatch, useAppSelector } from 'src/app/hooks';
import {
  setHasTranslationData,
  setIsTranslated,
  toggleIsTranslated
} from 'src/features/translateArticle';

interface TranslateArticleResponse {
  translation_translateArticle: {
    title: string;
    content: string;
  };
}

export interface TranslateArticlePayload {
  title: string;
  content: string;
}

export const useTranslateArticle = () => {
  const { handleClickAlert } = useAlert();
  const dispatch = useAppDispatch();
  const { isTranslated, hasTranslationData } = useAppSelector(
    (state) => state.storeTranslateArticle
  );

  const [translateArticle, resTranslateArticle] = useLazyQuery<
    TranslateArticleResponse,
    TranslateArticlePayload
  >(TRANSLATE_ARTICLE, {
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      dispatch(setHasTranslationData(true));
      dispatch(setIsTranslated(true));
    },
    onError: (error) => {
      handleClickAlert({
        message: 'Failed to translate article, please try again later.',
        severity: 'error',
        horizontal: 'center',
        vertical: 'top'
      });
    }
  });

  const useGetTranslatedArticleContent = (
    options: QueryHookOptions<TranslateArticleResponse, TranslateArticlePayload>
  ) => {
    const result = useQuery<TranslateArticleResponse, TranslateArticlePayload>(
      TRANSLATE_ARTICLE,
      options
    );

    // If data exists in cache and hasn't been marked, mark it
    if (result.data?.translation_translateArticle && !hasTranslationData) {
      dispatch(setHasTranslationData(true));
    }

    return result;
  };

  const handleToggleTranslation = () => {
    if (hasTranslationData) {
      dispatch(toggleIsTranslated());
    }
  };

  const handleClickTranslateButton = (text: TranslateArticlePayload) => {
    // Only fetch if we don't already have translation data
    if (!hasTranslationData) {
      translateArticle({
        variables: text
      });
    } else {
      // If we already have the data, just toggle
      handleToggleTranslation();
    }
  };

  return {
    translateArticle,
    resTranslateArticle,
    useGetTranslatedArticleContent,
    isTranslated,
    hasTranslationData,
    handleToggleTranslation,
    handleClickTranslateButton
  };
};
